import { Clipboard } from '@angular/cdk/clipboard';
import { Component, inject, Signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ForecastVersionResponse } from '@api-response-types/forecast-version.response';
import { Timespan } from '@api-types/types/timespan.type';
import { DescriptionComponent } from '@components/base-elements/description.component';
import {
  H2Component,
  PmDialogComponent,
  PmIconComponent,
  SubtitleComponent,
} from '@libs/ui';

import { selectIsLoggedInUserAdmin } from '@core/store/auth/auth.selectors';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ForecastsHttpClient } from 'demand-forecasting/services/http/forecast-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import {
  selectForecastVersionByForecastId,
  selectForecastVersionByQueryParam,
  selectLatestForecastVersionByConfigurationId,
} from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import { selectVersionId } from 'demand-forecasting/store/router-state/router.selectors';
import { LucideCopy } from '@lucide/angular';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'pm-forecast-dialog-dialog',
  template: `
    @let isAdmin = isLoggedInUserAdmin();
    @let config = this.configuration();
    @let version = this.version();
    @if (config && version) {
      <pm-dialog
        dialogTitle="RESULT.SIDEBAR.DESCRIPTION"
        [secondaryCtaButtonConfig]="{ text: 'UTILITY.CLOSE' }"
        contentClass="gap-8"
        [primaryCtaButtonConfig]="{ visible: false }"
      >
        <div class="flex flex-col gap-2">
          <pm-h2>
            {{ 'RESULT.DESCRIPTION.NAME' | translate }}
          </pm-h2>
          <div>
            <pm-subtitle>
              {{ config.name }}
            </pm-subtitle>
            <pm-description>
              @if (config.description) {
                {{ config.description }}
              } @else {
                {{ 'RESULT.DESCRIPTION.NO_DESCRIPTION' | translate }}
              }
            </pm-description>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <pm-h2>
            {{ 'RESULT.DESCRIPTION.MAPPING_DATA' | translate }}
          </pm-h2>
          <div>
            <pm-subtitle>
              {{ config.datasource.name }}
            </pm-subtitle>
            <pm-description>
              @if (config.considerEventsInForecast) {
                {{ 'RESULT.DESCRIPTION.WITH_EVENT_PLANNER' | translate }}
              } @else {
                {{ 'RESULT.DESCRIPTION.WITHOUT_EVENT_PLANNER' | translate }}
              }
            </pm-description>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <pm-h2>
            {{ 'RESULT.DESCRIPTION.CONFIGURATION_HEADING' | translate }}
          </pm-h2>
          <div>
            <pm-subtitle>
              {{ 'Configuration ID' }}
            </pm-subtitle>
            <div class="flex flex-row justify-between items-center">
              <pm-description>
                {{ config.id }}
              </pm-description>
              <pm-icon
                [icon]="copyIcon"
                (click)="copyToClipboardWithToastNotification(config.id || '')"
                class="cursor-pointer"
              />
            </div>
          </div>
          <div>
            <pm-subtitle>
              {{ 'RESULT.DESCRIPTION.CREATION_DATE' | translate }}
            </pm-subtitle>
            <pm-description>
              {{ this.formatDateTimeBasedOnLanguage(config.createdAt) }}
            </pm-description>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <pm-h2>
            {{ 'RESULT.DESCRIPTION.VERSION_HEADING' | translate }}
          </pm-h2>
          <div>
            <pm-subtitle>
              {{ 'Forecast ID' }}
            </pm-subtitle>
            <div class="flex flex-row justify-between items-center">
              <pm-description>
                {{ version.forecastId }}
              </pm-description>
              <pm-icon
                [icon]="copyIcon"
                (click)="
                  copyToClipboardWithToastNotification(version.forecastId || '')
                "
                class="cursor-pointer"
              />
            </div>
          </div>
          <div>
            <pm-subtitle>
              {{ 'RESULT.DESCRIPTION.LAST_DATASOURCE_UPDATE' | translate }}
            </pm-subtitle>
            <pm-description>
              {{
                this.formatDateTimeBasedOnLanguage(
                  version.updatedAtFromDatasourceInfo
                )
              }}
            </pm-description>
          </div>
          <div>
            <pm-subtitle>
              {{ 'RESULT.DESCRIPTION.START_OF_FORECAST_VALUES' | translate }}
            </pm-subtitle>
            <pm-description>
              {{
                this.formatDateTimeBasedOnLanguage(
                  this.timespan?.future_start_date
                )
              }}
            </pm-description>
          </div>
          <div>
            <pm-subtitle>
              {{
                'RESULT.DESCRIPTION.START_OF_FORECAST_CALCULATION' | translate
              }}
            </pm-subtitle>
            <pm-description>
              {{ this.formatDateTimeBasedOnLanguage(version.createdAt) }}
            </pm-description>
          </div>
          @if (!selectedVersionId() && latestVersion(); as latestVersion) {
            <div>
              <pm-subtitle>
                {{ 'RESULT.DESCRIPTION.DATE_OF_LATEST_VERSION' | translate }}
              </pm-subtitle>
              <pm-description>
                {{
                  this.formatDateTimeBasedOnLanguage(latestVersion.createdAt)
                }}
              </pm-description>
            </div>
          }
          @if (isAdmin) {
            <div>
              <pm-subtitle>
                {{ 'RESULT.DESCRIPTION.CALCULATION_DURATION' | translate }}
              </pm-subtitle>
              <pm-description>
                {{ runDurationDescription }}
              </pm-description>
            </div>
            @if (version.mlModelForecastId) {
              <div>
                <pm-subtitle>
                  {{ 'RESULT.DESCRIPTION.ML_MODEL_CREATION_DATE' | translate }}
                </pm-subtitle>
                <pm-description
                  >{{ mlModelCreationDateDescription }}
                </pm-description>
              </div>
            }
          }
        </div>
      </pm-dialog>
    }
  `,
  imports: [
    PmDialogComponent,
    TranslateModule,
    H2Component,
    DescriptionComponent,
    SubtitleComponent,
    PmIconComponent,
  ],
})
export class ForecastDetailsDialogComponent {
  private readonly forecastHttpClient = inject(ForecastsHttpClient);
  private readonly dateUtilsService = inject(DateUtilsService);

  private readonly store = inject(Store);
  private readonly clipboard = inject(Clipboard);
  private readonly toast = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  protected data = inject(MAT_DIALOG_DATA);
  timespan: Timespan | undefined;

  protected readonly copyIcon = LucideCopy;

  configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationById(this.data.configurationId),
  );

  selectedVersionId = this.store.selectSignal(selectVersionId);
  version: Signal<ForecastVersionResponse | undefined>;
  latestVersion = this.store.selectSignal(
    selectLatestForecastVersionByConfigurationId(this.data.configurationId),
  );
  protected isLoggedInUserAdmin = this.store.selectSignal(
    selectIsLoggedInUserAdmin,
  );
  constructor() {
    if (this.selectedVersionId()) {
      this.version = this.store.selectSignal(selectForecastVersionByQueryParam);
    } else {
      this.version = this.latestVersion;
    }
    const forecastId = this.version()?.forecastId;
    if (forecastId) {
      this.forecastHttpClient.getTimespan(forecastId).subscribe({
        next: (timespan) => {
          this.timespan = timespan;
        },
        error: (error) => {
          console.error(error);
        },
      });
    }
  }

  copyToClipboardWithToastNotification(text: string): void {
    this.clipboard.copy(text);
    this.toast.success(
      undefined,
      this.translate.instant('UTILITY.TEXT_COPIED_TO_CLIPBOARD_SUCCESSFULLY'),
    );
  }

  formatDateTimeBasedOnLanguage(dateString?: string): string {
    if (dateString) {
      return this.dateUtilsService.formatDateTimeBasedOnLanguage(dateString);
    }
    return '';
  }

  get runDurationDescription(): string {
    const runDurationSeconds = this.version()?.runDuration;
    if (!runDurationSeconds) {
      return this.translate.instant(
        'RESULT.DESCRIPTION.CALCULATION_DURATION_UNKNOWN',
      );
    }
    return this.dateUtilsService.convertSecondsToHoursMinutesSecondsString(
      runDurationSeconds,
    );
  }

  get mlModelCreationDateDescription(): string {
    const mlModelForecastId = this.version()?.mlModelForecastId;
    if (mlModelForecastId) {
      const forecastVersionOfMlModel = this.store.selectSignal(
        selectForecastVersionByForecastId(mlModelForecastId),
      );

      const forecastVersionOfModel = forecastVersionOfMlModel();
      if (forecastVersionOfModel) {
        return `${this.dateUtilsService.formatDateTimeBasedOnLanguage(forecastVersionOfModel.createdAt)}`;
      }
    }
    return '';
  }
}
